import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
	IMapFeatureLayer,
	IMapFeatureSource,
	IMapFeaturesActionPayload,
	IMapFeaturesState,
	IMapFeatureSourcePayload,
	IMapFeatureLayerPayload,
	IPreviewFiber
} from '../../interfaces';

import mapFeaturesState from './state';

export const mapFeaturesSlice = createSlice({
	initialState: mapFeaturesState,
	name: 'mapFeatures',
	reducers: {
		addPreviewFile: {
			prepare: (previewFile: IPreviewFiber) => {
				return { payload: { previewFile } };
			},
			reducer: (state: IMapFeaturesState, action: PayloadAction<IMapFeaturesActionPayload>) => {
				const { previewFile } = action.payload;

				state.previewFile = previewFile;
			}
		},
		errorReceiveCreateLayer: (state: IMapFeaturesState) => {
			state.requestingCreateLayer = false;
		},
		errorReceiveCreateSource: (state: IMapFeaturesState) => {
			state.requestingCreateSource = false;
		},
		errorReceiveLayers: (state: IMapFeaturesState) => {
			state.layers = undefined;
			state.requestingLayers = false;
		},
		errorReceiveRemoveLayer: (state: IMapFeaturesState) => {
			state.requestingRemoveLayer = false;
		},
		errorReceiveRemoveSource: (state: IMapFeaturesState) => {
			state.requestingRemoveSource = false;
		},
		errorReceiveSources: (state: IMapFeaturesState) => {
			state.sources = undefined;
			state.requestingSources = false;
		},
		errorReceiveUpdateLayer: (state: IMapFeaturesState) => {
			state.requestingUpdateLayer = false;
		},
		errorReceiveUpdateSource: (state: IMapFeaturesState) => {
			state.requestingUpdateSource = false;
		},
		errorReceiveUpdateTileserver: (state: IMapFeaturesState) => {
			state.requestingUpdateTileserver = false;
		},
		failedReceiveCreateLayer: (state: IMapFeaturesState) => {
			state.requestingCreateLayer = false;
		},
		failedReceiveCreateSource: (state: IMapFeaturesState) => {
			state.requestingCreateSource = false;
		},
		failedReceiveLayers: (state: IMapFeaturesState) => {
			state.layers = undefined;
			state.requestingLayers = false;
		},
		failedReceiveRemoveLayer: (state: IMapFeaturesState) => {
			state.requestingRemoveLayer = false;
		},
		failedReceiveRemoveSource: (state: IMapFeaturesState) => {
			state.requestingRemoveSource = false;
		},
		failedReceiveSources: (state: IMapFeaturesState) => {
			state.sources = undefined;
			state.requestingSources = false;
		},
		failedReceiveUpdateLayer: (state: IMapFeaturesState) => {
			state.requestingUpdateLayer = false;
		},
		failedReceiveUpdateSource: (state: IMapFeaturesState) => {
			state.requestingUpdateSource = false;
		},
		failedReceiveUpdateTileserver: (state: IMapFeaturesState) => {
			state.requestingUpdateTileserver = false;
		},
		receiveCreateLayer: (state: IMapFeaturesState) => {
			state.requestingCreateLayer = false;
		},
		receiveCreateSource: (state: IMapFeaturesState) => {
			state.requestingCreateSource = false;
		},
		receiveLayers: {
			prepare: (layers: Array<IMapFeatureLayer>) => {
				return { payload: { layers } };
			},
			reducer: (state: IMapFeaturesState, action: PayloadAction<IMapFeaturesActionPayload>) => {
				const { layers } = action.payload;

				state.layers = layers;
				state.requestingLayers = false;
			}
		},
		receiveRemoveLayer: (state: IMapFeaturesState) => {
			state.requestingRemoveLayer = false;
		},
		receiveRemoveSource: (state: IMapFeaturesState) => {
			state.requestingRemoveSource = false;
		},
		receiveSources: {
			prepare: (sources: Array<IMapFeatureSource>) => {
				return { payload: { sources } };
			},
			reducer: (state: IMapFeaturesState, action: PayloadAction<IMapFeaturesActionPayload>) => {
				const { sources } = action.payload;

				state.sources = sources;
				state.requestingSources = false;
			}
		},
		receiveUpdateLayer: (state: IMapFeaturesState) => {
			state.requestingUpdateLayer = false;
		},
		receiveUpdateSource: (state: IMapFeaturesState) => {
			state.requestingUpdateSource = false;
		},
		receiveUpdateTileserver: (state: IMapFeaturesState) => {
			state.requestingUpdateTileserver = false;
		},
		requestCreateLayer: {
			prepare: (formData: IMapFeatureLayerPayload, sourcePayload?: IMapFeatureSourcePayload) => {
				return { payload: { formData, sourcePayload } };
			},
			reducer: (state: IMapFeaturesState) => {
				state.requestingCreateLayer = true;
			}
		},
		requestCreateSource: {
			prepare: (sourcePayload: IMapFeatureSourcePayload) => {
				return { payload: { sourcePayload } };
			},
			reducer: (state: IMapFeaturesState) => {
				state.requestingCreateSource = true;
			}
		},
		requestLayers: (state: IMapFeaturesState) => {
			state.requestingLayers = true;
		},
		requestRemoveLayer: {
			prepare: (layerID: string) => {
				return { payload: { layerID } };
			},
			reducer: (state: IMapFeaturesState) => {
				state.requestingRemoveLayer = true;
			}
		},
		requestRemoveSource: {
			prepare: (sourceID: string) => {
				return { payload: { sourceID } };
			},
			reducer: (state: IMapFeaturesState) => {
				state.requestingRemoveSource = true;
			}
		},
		requestSources: (state: IMapFeaturesState) => {
			state.requestingSources = true;
		},
		requestUpdateLayer: {
			prepare: (layerID: string, formData: IMapFeatureLayerPayload) => {
				return { payload: { formData, layerID } };
			},
			reducer: (state: IMapFeaturesState) => {
				state.requestingUpdateLayer = true;
			}
		},
		requestUpdateSource: {
			prepare: (sourceID: string, sourcePayload: IMapFeatureSourcePayload) => {
				return { payload: { sourceID, sourcePayload } };
			},
			reducer: (state: IMapFeaturesState) => {
				state.requestingUpdateSource = true;
			}
		},
		requestUpdateTileserver: {
			prepare: (assetPath: string, sourcePayload?: IMapFeatureSourcePayload) => {
				return { payload: { assetPath, sourcePayload } };
			},
			reducer: (state: IMapFeaturesState) => {
				state.requestingUpdateTileserver = true;
			}
		},
		resetMapFeatures: () => mapFeaturesState
	}
});

export const {
	addPreviewFile,
	errorReceiveCreateLayer,
	errorReceiveCreateSource,
	errorReceiveLayers,
	errorReceiveRemoveLayer,
	errorReceiveRemoveSource,
	errorReceiveSources,
	errorReceiveUpdateLayer,
	errorReceiveUpdateSource,
	errorReceiveUpdateTileserver,
	failedReceiveCreateLayer,
	failedReceiveCreateSource,
	failedReceiveLayers,
	failedReceiveRemoveLayer,
	failedReceiveRemoveSource,
	failedReceiveSources,
	failedReceiveUpdateLayer,
	failedReceiveUpdateSource,
	failedReceiveUpdateTileserver,
	receiveCreateLayer,
	receiveCreateSource,
	receiveLayers,
	receiveRemoveLayer,
	receiveRemoveSource,
	receiveSources,
	receiveUpdateLayer,
	receiveUpdateSource,
	receiveUpdateTileserver,
	requestCreateLayer,
	requestCreateSource,
	requestLayers,
	requestRemoveLayer,
	requestRemoveSource,
	requestSources,
	requestUpdateLayer,
	requestUpdateSource,
	requestUpdateTileserver,
	resetMapFeatures
} = mapFeaturesSlice.actions;

export default mapFeaturesSlice.reducer;
